import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase';
import { doc, getDoc, setDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import '../../../App.css';
import './MyAccount.css';
import Footer from '../../Footer';

const emptyAddress = {
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
};

export default function MyAccount() {
    const { currentUser, isAdmin, loading: authLoading, logout, updateUserProfile, resetPassword } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
    const [displayName, setDisplayName] = useState('');

    useEffect(() => {
        const tab = searchParams.get('tab');
        setActiveTab(tab || 'dashboard');
    }, [searchParams]);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const [billingAddress, setBillingAddress] = useState({ ...emptyAddress });
    const [shippingAddress, setShippingAddress] = useState({ ...emptyAddress });
    const [editingBilling, setEditingBilling] = useState(false);
    const [editingShipping, setEditingShipping] = useState(false);
    const [billingForm, setBillingForm] = useState({ ...emptyAddress });
    const [shippingForm, setShippingForm] = useState({ ...emptyAddress });
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return; // wait for auth to resolve before deciding
        if (!currentUser) {
            navigate('/sign-up');
            return;
        }
        setDisplayName(currentUser.displayName || '');
        loadAddresses();
        loadOrders();
    }, [currentUser, authLoading, navigate]);

    async function loadAddresses() {
        if (!currentUser) return;
        try {
            const snap = await getDoc(doc(db, 'users', currentUser.uid));
            if (snap.exists()) {
                const data = snap.data();
                if (data.billingAddress) setBillingAddress(data.billingAddress);
                if (data.shippingAddress) setShippingAddress(data.shippingAddress);
            }
        } catch {
            // silently fail
        }
    }

    async function loadOrders() {
        if (!currentUser) return;
        try {
            const q = query(
                collection(db, 'orders'),
                where('userId', '==', currentUser.uid),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch {
            // silently fail
        }
        setOrdersLoading(false);
    }

    if (!currentUser) return null;

    const memberSince = currentUser.metadata?.creationTime
        ? new Date(currentUser.metadata.creationTime).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : 'Unknown';

    async function handleSaveName(e) {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            await updateUserProfile(displayName.trim());
            setMessage('Display name updated successfully');
            setIsEditing(false);
        } catch (err) {
            setError('Failed to update display name');
        }
        setLoading(false);
    }

    function handleCancelEdit() {
        setDisplayName(currentUser.displayName || '');
        setIsEditing(false);
        setError('');
        setMessage('');
    }

    async function handleChangePassword() {
        setError('');
        setMessage('');
        try {
            await resetPassword(currentUser.email);
            setMessage('Password reset email sent to ' + currentUser.email);
        } catch {
            setError('Failed to send password reset email');
        }
    }

    async function handleLogout() {
        try {
            await logout();
            navigate('/');
        } catch {
            setError('Failed to log out');
        }
    }

    async function handleSaveAddress(type) {
        setError('');
        setMessage('');
        setLoading(true);
        const form = type === 'billing' ? billingForm : shippingForm;
        try {
            await setDoc(doc(db, 'users', currentUser.uid), {
                [type === 'billing' ? 'billingAddress' : 'shippingAddress']: form,
            }, { merge: true });
            if (type === 'billing') {
                setBillingAddress({ ...form });
                setEditingBilling(false);
            } else {
                setShippingAddress({ ...form });
                setEditingShipping(false);
            }
            setMessage(`${type === 'billing' ? 'Billing' : 'Shipping'} address updated successfully`);
        } catch {
            setError(`Failed to update ${type} address`);
        }
        setLoading(false);
    }

    function startEditAddress(type) {
        if (type === 'billing') {
            setBillingForm({ ...billingAddress });
            setEditingBilling(true);
        } else {
            setShippingForm({ ...shippingAddress });
            setEditingShipping(true);
        }
    }

    function formatAddress(addr) {
        if (!addr.firstName && !addr.street && !addr.city) return null;
        const lines = [];
        if (addr.firstName || addr.lastName) lines.push(`${addr.firstName} ${addr.lastName}`.trim());
        if (addr.street) lines.push(addr.street);
        if (addr.city || addr.state || addr.zip) {
            lines.push(`${addr.city}${addr.state ? ', ' + addr.state : ''}${addr.zip ? ' ' + addr.zip : ''}`);
        }
        if (addr.country) lines.push(addr.country);
        if (addr.phone) lines.push('T: ' + addr.phone);
        return lines;
    }

    function renderAddressForm(form, setForm) {
        const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
        return (
            <div className='myaccount-address-form'>
                <div className='myaccount-address-row'>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>First Name</label>
                        <input className='myaccount-input' value={form.firstName} onChange={(e) => update('firstName', e.target.value)} />
                    </div>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>Last Name</label>
                        <input className='myaccount-input' value={form.lastName} onChange={(e) => update('lastName', e.target.value)} />
                    </div>
                </div>
                <div className='myaccount-address-field'>
                    <label className='myaccount-label'>Street Address</label>
                    <input className='myaccount-input' value={form.street} onChange={(e) => update('street', e.target.value)} />
                </div>
                <div className='myaccount-address-row'>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>City</label>
                        <input className='myaccount-input' value={form.city} onChange={(e) => update('city', e.target.value)} />
                    </div>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>State</label>
                        <input className='myaccount-input' value={form.state} onChange={(e) => update('state', e.target.value)} />
                    </div>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>ZIP Code</label>
                        <input className='myaccount-input' value={form.zip} onChange={(e) => update('zip', e.target.value)} />
                    </div>
                </div>
                <div className='myaccount-address-row'>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>Country</label>
                        <input className='myaccount-input' value={form.country} onChange={(e) => update('country', e.target.value)} />
                    </div>
                    <div className='myaccount-address-field'>
                        <label className='myaccount-label'>Phone</label>
                        <input className='myaccount-input' value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                    </div>
                </div>
            </div>
        );
    }

    const sidebarItems = [
        { id: 'dashboard', label: 'Account Dashboard' },
        { id: 'info', label: 'Account Information' },
        { id: 'address', label: 'Address Book' },
        { id: 'orders', label: 'My Orders' },
    ];

    const isAdminUser = currentUser && isAdmin && currentUser.email === 'info@offftheroast.com';

    const billingLines = formatAddress(billingAddress);
    const shippingLines = formatAddress(shippingAddress);

    return (
        <>
            <div className='myaccount-page'>
                <div className='myaccount-layout'>
                    {/* Sidebar */}
                    <aside className='myaccount-sidebar'>
                        <ul className='myaccount-sidebar-nav'>
                            {sidebarItems
                                .filter((item) => !(isAdminUser && item.id === 'orders'))
                                .map((item) => (
                                <li key={item.id}>
                                    <button
                                        className={`myaccount-sidebar-link ${activeTab === item.id ? 'active' : ''}`}
                                        onClick={() => { setActiveTab(item.id); setMessage(''); setError(''); }}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            ))}
                            {isAdminUser && (
                                <>
                                    <li>
                                        <button
                                            className='myaccount-sidebar-link'
                                            onClick={() => navigate('/admin-menu')}
                                        >
                                            Menu Editor
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className='myaccount-sidebar-link'
                                            onClick={() => navigate('/admin-orders')}
                                        >
                                            Drink Orders Tracker
                                        </button>
                                    </li>
                                </>
                            )}
                            <li>
                                <button className='myaccount-sidebar-link' onClick={handleLogout}>
                                    Log Out
                                </button>
                            </li>
                        </ul>
                    </aside>

                    {/* Main Content */}
                    <main className='myaccount-main'>
                        {error && <div className='myaccount-error'>{error}</div>}
                        {message && <div className='myaccount-message'>{message}</div>}

                        {/* Dashboard Tab */}
                        {activeTab === 'dashboard' && (
                            <>
                                <h1 className='myaccount-page-title'>My Dashboard</h1>

                                <div className='myaccount-section-block'>
                                    <h2 className='myaccount-section-title'>Account Information</h2>
                                    <div className='myaccount-two-col'>
                                        <div className='myaccount-col'>
                                            <h3 className='myaccount-col-title'>Contact Information</h3>
                                            <p className='myaccount-col-value'>{currentUser.displayName || 'Not set'}</p>
                                            <p className='myaccount-col-value'>{currentUser.email}</p>
                                            <div className='myaccount-col-links'>
                                                <button className='myaccount-link' onClick={() => { setActiveTab('info'); setIsEditing(true); }}>Edit</button>
                                                <span className='myaccount-link-divider'>|</span>
                                                <button className='myaccount-link' onClick={handleChangePassword}>Change Password</button>
                                            </div>
                                        </div>
                                        <div className='myaccount-col'>
                                            <h3 className='myaccount-col-title'>Member Since</h3>
                                            <p className='myaccount-col-value'>{memberSince}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className='myaccount-section-block'>
                                    <h2 className='myaccount-section-title'>
                                        Address Book
                                        <button className='myaccount-link myaccount-manage-link' onClick={() => setActiveTab('address')}>Manage Addresses</button>
                                    </h2>
                                    <div className='myaccount-two-col'>
                                        <div className='myaccount-col'>
                                            <h3 className='myaccount-col-title'>Default Billing Address</h3>
                                            {billingLines ? (
                                                <>
                                                    {billingLines.map((line, i) => <p key={i} className='myaccount-col-value'>{line}</p>)}
                                                    <div className='myaccount-col-links'>
                                                        <button className='myaccount-link' onClick={() => { setActiveTab('address'); startEditAddress('billing'); }}>Edit Address</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <p className='myaccount-col-value myaccount-empty'>No billing address set.</p>
                                            )}
                                        </div>
                                        <div className='myaccount-col'>
                                            <h3 className='myaccount-col-title'>Default Shipping Address</h3>
                                            {shippingLines ? (
                                                <>
                                                    {shippingLines.map((line, i) => <p key={i} className='myaccount-col-value'>{line}</p>)}
                                                    <div className='myaccount-col-links'>
                                                        <button className='myaccount-link' onClick={() => { setActiveTab('address'); startEditAddress('shipping'); }}>Edit Address</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <p className='myaccount-col-value myaccount-empty'>No shipping address set.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Account Information Tab */}
                        {activeTab === 'info' && (
                            <>
                                <h1 className='myaccount-page-title'>Account Information</h1>

                                <div className='myaccount-section-block'>
                                    <h2 className='myaccount-section-title'>Contact Information</h2>
                                    <div className='myaccount-info-form'>
                                        <div className='myaccount-field'>
                                            <label className='myaccount-label'>Display Name</label>
                                            {isEditing ? (
                                                <form onSubmit={handleSaveName}>
                                                    <input
                                                        type='text'
                                                        className='myaccount-input'
                                                        value={displayName}
                                                        onChange={(e) => setDisplayName(e.target.value)}
                                                        placeholder='Enter your name'
                                                        autoFocus
                                                    />
                                                    <div className='myaccount-edit-actions'>
                                                        <button type='submit' className='myaccount-save-btn' disabled={loading}>
                                                            {loading ? 'SAVING...' : 'SAVE'}
                                                        </button>
                                                        <button type='button' className='myaccount-cancel-btn' onClick={handleCancelEdit}>
                                                            CANCEL
                                                        </button>
                                                    </div>
                                                </form>
                                            ) : (
                                                <div className='myaccount-value-row'>
                                                    <span className='myaccount-value'>{currentUser.displayName || 'Not set'}</span>
                                                    <button className='myaccount-link' onClick={() => setIsEditing(true)}>Edit</button>
                                                </div>
                                            )}
                                        </div>

                                        <div className='myaccount-field'>
                                            <label className='myaccount-label'>Email</label>
                                            <span className='myaccount-value'>{currentUser.email}</span>
                                        </div>

                                        <div className='myaccount-field'>
                                            <label className='myaccount-label'>Password</label>
                                            <div className='myaccount-value-row'>
                                                <span className='myaccount-value'>********</span>
                                                <button className='myaccount-link' onClick={handleChangePassword}>Change Password</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Address Book Tab */}
                        {activeTab === 'address' && (
                            <>
                                <h1 className='myaccount-page-title'>Address Book</h1>

                                <div className='myaccount-section-block'>
                                    <h2 className='myaccount-section-title'>Default Billing Address</h2>
                                    {editingBilling ? (
                                        <>
                                            {renderAddressForm(billingForm, setBillingForm)}
                                            <div className='myaccount-edit-actions'>
                                                <button className='myaccount-save-btn' disabled={loading} onClick={() => handleSaveAddress('billing')}>
                                                    {loading ? 'SAVING...' : 'SAVE'}
                                                </button>
                                                <button className='myaccount-cancel-btn' onClick={() => setEditingBilling(false)}>
                                                    CANCEL
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {billingLines ? (
                                                billingLines.map((line, i) => <p key={i} className='myaccount-col-value'>{line}</p>)
                                            ) : (
                                                <p className='myaccount-empty'>No billing address set.</p>
                                            )}
                                            <div className='myaccount-col-links'>
                                                <button className='myaccount-link' onClick={() => startEditAddress('billing')}>Edit Address</button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className='myaccount-section-block'>
                                    <h2 className='myaccount-section-title'>Default Shipping Address</h2>
                                    {editingShipping ? (
                                        <>
                                            {renderAddressForm(shippingForm, setShippingForm)}
                                            <div className='myaccount-edit-actions'>
                                                <button className='myaccount-save-btn' disabled={loading} onClick={() => handleSaveAddress('shipping')}>
                                                    {loading ? 'SAVING...' : 'SAVE'}
                                                </button>
                                                <button className='myaccount-cancel-btn' onClick={() => setEditingShipping(false)}>
                                                    CANCEL
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {shippingLines ? (
                                                shippingLines.map((line, i) => <p key={i} className='myaccount-col-value'>{line}</p>)
                                            ) : (
                                                <p className='myaccount-empty'>No shipping address set.</p>
                                            )}
                                            <div className='myaccount-col-links'>
                                                <button className='myaccount-link' onClick={() => startEditAddress('shipping')}>Edit Address</button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        )}

                        {/* My Orders Tab */}
                        {activeTab === 'orders' && (
                            <>
                                <h1 className='myaccount-page-title'>My Orders</h1>

                                <div className='myaccount-section-block'>
                                    {ordersLoading ? (
                                        <p className='myaccount-empty'>Loading orders...</p>
                                    ) : orders.length === 0 ? (
                                        <p className='myaccount-empty'>You have placed no orders yet.</p>
                                    ) : (
                                        <div className='myaccount-orders-table-wrap'>
                                            <table className='myaccount-orders-table'>
                                                <thead>
                                                    <tr>
                                                        <th>Order #</th>
                                                        <th>Date</th>
                                                        <th>Total</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orders.map((order) => (
                                                        <tr key={order.id}>
                                                            <td>{order.orderNumber || order.id}</td>
                                                            <td>{order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('en-US') : '—'}</td>
                                                            <td>${(order.total || 0).toFixed(2)}</td>
                                                            <td>
                                                                <span className={`myaccount-order-status myaccount-order-status--${(order.status || 'pending').toLowerCase()}`}>
                                                                    {order.status || 'Pending'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button className='myaccount-link'>View</button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </main>
                </div>
            </div>

            <Footer />
        </>
    );
}
